import { Gift } from "lucide-react";
import { Badge, Button, Input } from "@/components/ui";
import { MINOR_UNITS_PER_UNIT, formatCurrency } from "@/lib/utils";

interface LoyaltyRedemptionSectionProps {
  customerPoints: number;
  loyaltyRedemptionRate: number;
  loyaltyRedemptionValue: number;
  pointsToRedeem: number;
  maxRedeemablePoints: number;
  pointsDiscount: number;
  currencySymbol: string;
  onChange: (next: number) => void;
}

export function LoyaltyRedemptionSection({
  customerPoints,
  loyaltyRedemptionRate,
  loyaltyRedemptionValue,
  pointsToRedeem,
  maxRedeemablePoints,
  pointsDiscount,
  currencySymbol,
  onChange,
}: LoyaltyRedemptionSectionProps) {
  const clampToRedeemable = (points: number) =>
    Math.min(Math.max(0, points), maxRedeemablePoints);

  return (
    <div className="space-y-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Gift className="h-5 w-5 text-amber-600" />
          <span className="font-medium text-amber-800">Loyalty Points</span>
        </div>
        <Badge variant="secondary" className="bg-amber-100 text-amber-800">
          {customerPoints} pts available
        </Badge>
      </div>
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm text-amber-700">
          <span>
            Use points ({loyaltyRedemptionRate} pts ={" "}
            {formatCurrency(
              loyaltyRedemptionValue * MINOR_UNITS_PER_UNIT,
              currencySymbol,
            )}
            )
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Input
            type="number"
            min={0}
            max={maxRedeemablePoints}
            value={pointsToRedeem || ""}
            onChange={(e) =>
              onChange(clampToRedeemable(parseInt(e.target.value) || 0))
            }
            className="w-24"
            placeholder="0"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onChange(maxRedeemablePoints)}
            disabled={maxRedeemablePoints === 0}
          >
            Use Max
          </Button>
          {pointsToRedeem > 0 && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onChange(0)}
            >
              Clear
            </Button>
          )}
        </div>
        {pointsDiscount > 0 && (
          <p className="text-sm font-medium text-green-600">
            Discount: -{formatCurrency(pointsDiscount, currencySymbol)}
          </p>
        )}
      </div>
    </div>
  );
}
